import React, { useEffect, useMemo, useState } from 'react';
import { useVaccinationSchedule, useMarkVaccinationCompleted } from '../hooks/useVaccinations';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as ScheduledDotIcon,
  FilterList as FilterIcon,
  NotificationsActive as DueNowIcon,
  Schedule as ScheduleIcon,
  TaskAlt as MarkCompleteIcon,
  Vaccines as VaccinesIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface ScheduleEvent {
  day: number;
  key: string;
  type: string;
  name: string;
  desc: string;
  status: 'COMPLETED' | 'OVERDUE' | 'DUE NOW' | 'UPCOMING' | 'SCHEDULED';
  scheduled_date: string;
}

interface BatchSchedule {
  batch: string;
  procurement_id: number;
  date_delivered: string;
  current_age_days: number;
  schedule: ScheduleEvent[];
  progress: { completed: number; total: number; percent: number };
}

const STATUS_CONFIG = {
  COMPLETED: {
    color: '#4caf50',
    bg: 'rgba(76,175,80,0.07)',
    label: 'Completed',
    Icon: CheckCircleIcon,
  },
  OVERDUE: { color: '#f44336', bg: 'rgba(244,67,54,0.09)', label: 'Overdue', Icon: WarningIcon },
  'DUE NOW': { color: '#ff9800', bg: 'rgba(255,152,0,0.09)', label: 'Due Today', Icon: DueNowIcon },
  UPCOMING: {
    color: '#f5a623',
    bg: 'rgba(245,166,35,0.07)',
    label: 'Upcoming',
    Icon: ScheduleIcon,
  },
  SCHEDULED: {
    color: '#616161',
    bg: 'rgba(97,97,97,0.05)',
    label: 'Scheduled',
    Icon: ScheduledDotIcon,
  },
};

const TYPE_CONFIG: Record<string, { color: string; label: string }> = {
  Vaccine: { color: '#42a5f5', label: 'Vaccine' },
  Drug: { color: '#ab47bc', label: 'Drug' },
  Medication: { color: '#ec407a', label: 'Medication' },
  Coccidiostat: { color: '#26a69a', label: 'Coccidiostat' },
  Treatment: { color: '#ffa726', label: 'Treatment' },
  Management: { color: '#78909c', label: 'Management' },
};

const FILTERS = [
  { key: 'ALL', label: 'All', color: '#ff9800' },
  { key: 'OVERDUE', label: 'Overdue', color: '#f44336' },
  { key: 'DUE NOW', label: 'Due Today', color: '#ff9800' },
  { key: 'UPCOMING', label: 'Upcoming', color: '#f5a623' },
  { key: 'COMPLETED', label: 'Completed', color: '#4caf50' },
  { key: 'SCHEDULED', label: 'Scheduled', color: '#757575' },
] as const;

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const progressColor = (pct: number) => (pct >= 80 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#f44336');

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  accent: string;
  Icon: React.ElementType;
  sub?: string;
}> = ({ label, value, accent, Icon, sub }) => (
  <Paper
    sx={{
      p: 2.5,
      height: '100%',
      background: 'linear-gradient(145deg, #181818 0%, #1d1d1d 100%)',
      border: `1px solid ${accent}28`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.25s ease',
      '&:hover': {
        border: `1px solid ${accent}55`,
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 28px rgba(0,0,0,0.6), 0 0 0 1px ${accent}18`,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
        borderRadius: '10px 10px 0 0',
      },
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.45)',
            fontWeight: 700,
            letterSpacing: '1px',
            fontSize: '0.62rem',
            display: 'block',
            mb: 0.75,
          }}
        >
          {label.toUpperCase()}
        </Typography>
        <Typography
          variant="h4"
          sx={{ color: accent, fontWeight: 800, lineHeight: 1, mb: sub ? 0.5 : 0 }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}
          >
            {sub}
          </Typography>
        )}
      </Box>
      <Avatar
        sx={{
          bgcolor: `${accent}14`,
          color: accent,
          width: 42,
          height: 42,
          border: `1px solid ${accent}22`,
        }}
      >
        <Icon fontSize="small" />
      </Avatar>
    </Box>
  </Paper>
);

const VaccinationCalendar: React.FC = () => {
  const [filter, setFilter] = useState<string>('ALL');
  const [selectedBatch, setSelectedBatch] = useState<BatchSchedule | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    batch: BatchSchedule;
    event: ScheduleEvent;
  } | null>(null);

  const { data: rawBatches = [], isFetching, error, refetch } = useVaccinationSchedule();
  const batches = rawBatches as BatchSchedule[];
  const mutation = useMarkVaccinationCompleted();

  // Auto-select the most urgent batch on load
  useEffect(() => {
    if (batches.length === 0) return;
    if (
      selectedBatch &&
      batches.find(
        (b) =>
          b.procurement_id === selectedBatch.procurement_id &&
          b.date_delivered === selectedBatch.date_delivered
      )
    )
      return;
    const urgent = batches.find((b) =>
      b.schedule.some((e) => e.status === 'OVERDUE' || e.status === 'DUE NOW')
    );
    setSelectedBatch(urgent ?? batches[0]);
  }, [batches]);

  const globalStats = useMemo(() => {
    let completed = 0,
      overdue = 0,
      dueNow = 0,
      upcoming = 0,
      total = 0;
    for (const b of batches) {
      completed += b.progress.completed;
      overdue += b.schedule.filter((e) => e.status === 'OVERDUE').length;
      dueNow += b.schedule.filter((e) => e.status === 'DUE NOW').length;
      upcoming += b.schedule.filter((e) => e.status === 'UPCOMING').length;
      total += b.progress.total;
    }
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, overdue, dueNow, upcoming, total, pct };
  }, [batches]);

  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: 0 };
    if (!selectedBatch) return counts;
    counts.ALL = selectedBatch.schedule.length;
    for (const e of selectedBatch.schedule) {
      counts[e.status] = (counts[e.status] ?? 0) + 1;
    }
    return counts;
  }, [selectedBatch]);

  const visibleSchedule = useMemo(() => {
    if (!selectedBatch) return [];
    return filter === 'ALL'
      ? selectedBatch.schedule
      : selectedBatch.schedule.filter((e) => e.status === filter);
  }, [selectedBatch, filter]);

  const handleMarkComplete = (batch: BatchSchedule, event: ScheduleEvent) => {
    mutation.mutate(
      {
        procurementId: batch.procurement_id,
        vaccinationKey: event.key,
        deliveryDate: batch.date_delivered,
      },
      {
        onSuccess: () => {
          refetch();
          setConfirmDialog(null);
        },
      }
    );
  };

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <VaccinesIcon sx={{ fontSize: 64, color: 'rgba(244,67,54,0.3)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#e0e0e0' }} gutterBottom>
            Failed to load schedule
          </Typography>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{ mt: 1, background: '#f44336', '&:hover': { background: '#d32f2f' } }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffcc02)' },
          }}
        />
      )}

      {/* ── Page header ── */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
          border: '1px solid rgba(255,152,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '35%',
            background:
              'radial-gradient(ellipse at right center, rgba(255,152,0,0.06) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'rgba(255,152,0,0.12)',
            color: '#ff9800',
            width: 56,
            height: 56,
            border: '1px solid rgba(255,152,0,0.25)',
          }}
        >
          <CalendarIcon />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.2 }}
          >
            Vaccination Schedule
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.52)', mt: 0.4 }}>
            Full health programme timeline · {batches.length} active batch
            {batches.length !== 1 ? 'es' : ''}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 800, lineHeight: 1 }}>
            {globalStats.pct}%
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}
          >
            overall done
          </Typography>
        </Box>
      </Box>

      {/* ── Global stats ── */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Completed"
            value={globalStats.completed}
            accent="#4caf50"
            Icon={CheckCircleIcon}
            sub={`of ${globalStats.total} events`}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Overdue"
            value={globalStats.overdue}
            accent="#f44336"
            Icon={WarningIcon}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Due Today"
            value={globalStats.dueNow}
            accent="#ff9800"
            Icon={DueNowIcon}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Upcoming"
            value={globalStats.upcoming}
            accent="#f5a623"
            Icon={ScheduleIcon}
          />
        </Grid>
      </Grid>

      {batches.length === 0 && !isFetching ? (
        <Paper
          sx={{
            p: 8,
            textAlign: 'center',
            background: '#141414',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2.5,
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: 'rgba(255,152,0,0.2)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#555' }}>
            No active batches
          </Typography>
          <Typography variant="body2" sx={{ color: '#444', mt: 0.5 }}>
            Delivered procurements will appear here with their full health schedule.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* ── Batch selector + batch header ── */}
          <Paper
            sx={{
              p: 3,
              mb: 2.5,
              background: 'linear-gradient(145deg, #161616 0%, #1b1b1b 100%)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
              {/* Autocomplete */}
              <Box sx={{ flex: '1 1 260px', minWidth: 200 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#555',
                    fontWeight: 700,
                    letterSpacing: '0.8px',
                    fontSize: '0.62rem',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  SELECT BATCH
                </Typography>
                <Autocomplete
                  options={batches}
                  value={selectedBatch ?? undefined}
                  onChange={(_, value) => {
                    setSelectedBatch(value);
                    setFilter('ALL');
                  }}
                  getOptionLabel={(option) => option.batch}
                  isOptionEqualToValue={(option, value) =>
                    option.procurement_id === value.procurement_id &&
                    option.date_delivered === value.date_delivered
                  }
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Choose a batch…"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#111',
                          borderRadius: 1.5,
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
                          '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                        },
                        '& .MuiAutocomplete-popupIndicator': { color: '#555' },
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const pct = option.progress.percent;
                    const pc = progressColor(pct);
                    const overdueN = option.schedule.filter((e) => e.status === 'OVERDUE').length;
                    return (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 2,
                          py: '10px !important',
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '0.85rem' }}
                          >
                            {option.batch}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            Day {option.current_age_days} · delivered{' '}
                            {formatDate(option.date_delivered)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography
                            sx={{ color: pc, fontWeight: 800, fontSize: '0.85rem', lineHeight: 1 }}
                          >
                            {pct}%
                          </Typography>
                          {overdueN > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#f44336', fontSize: '0.65rem' }}
                            >
                              {overdueN} overdue
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        bgcolor: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                        '& .MuiAutocomplete-option': {
                          px: 2,
                          '&:hover': { bgcolor: 'rgba(255,152,0,0.06)' },
                          '&[aria-selected="true"]': { bgcolor: 'rgba(255,152,0,0.1) !important' },
                        },
                      },
                    },
                  }}
                />
              </Box>

              {/* Selected batch meta */}
              {selectedBatch && (
                <Box sx={{ flex: '2 1 320px' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      fontWeight: 700,
                      letterSpacing: '0.8px',
                      fontSize: '0.62rem',
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    BATCH PROGRESS
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}
                    >
                      Day {selectedBatch.current_age_days} · delivered{' '}
                      {formatDate(selectedBatch.date_delivered)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: progressColor(selectedBatch.progress.percent),
                        fontWeight: 800,
                        fontSize: '0.78rem',
                      }}
                    >
                      {selectedBatch.progress.completed} / {selectedBatch.progress.total} (
                      {selectedBatch.progress.percent}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={selectedBatch.progress.percent}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(255,255,255,0.05)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${progressColor(selectedBatch.progress.percent)}, ${progressColor(selectedBatch.progress.percent)}99)`,
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    {(filterCounts['OVERDUE'] ?? 0) > 0 && (
                      <Chip
                        label={`${filterCounts['OVERDUE']} overdue`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(244,67,54,0.12)',
                          color: '#f44336',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 20,
                          border: '1px solid rgba(244,67,54,0.22)',
                        }}
                      />
                    )}
                    {(filterCounts['DUE NOW'] ?? 0) > 0 && (
                      <Chip
                        label="Due today"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,152,0,0.12)',
                          color: '#ff9800',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 20,
                          border: '1px solid rgba(255,152,0,0.22)',
                        }}
                      />
                    )}
                    {(filterCounts['UPCOMING'] ?? 0) > 0 && (
                      <Chip
                        label={`${filterCounts['UPCOMING']} upcoming`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(245,166,35,0.1)',
                          color: '#f5a623',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 20,
                          border: '1px solid rgba(245,166,35,0.2)',
                        }}
                      />
                    )}
                    {(filterCounts['COMPLETED'] ?? 0) > 0 && (
                      <Chip
                        label={`${filterCounts['COMPLETED']} done`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(76,175,80,0.1)',
                          color: '#4caf50',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 20,
                          border: '1px solid rgba(76,175,80,0.2)',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>

          {/* ── Filter chips ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <FilterIcon sx={{ color: '#444', fontSize: '0.9rem' }} />
            {FILTERS.map(({ key, label, color }) => {
              const active = filter === key;
              const count = filterCounts[key] ?? 0;
              return (
                <Chip
                  key={key}
                  label={`${label}${count > 0 ? ` · ${count}` : ''}`}
                  size="small"
                  onClick={() => setFilter(key)}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    height: 24,
                    bgcolor: active ? `${color}22` : 'rgba(255,255,255,0.03)',
                    color: active ? color : '#4a4a4a',
                    border: active ? `1px solid ${color}55` : '1px solid rgba(255,255,255,0.06)',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: `${color}16`, color, border: `1px solid ${color}40` },
                  }}
                />
              );
            })}
          </Box>

          {/* ── Timeline ── */}
          {visibleSchedule.length === 0 ? (
            <Paper
              sx={{
                p: 5,
                textAlign: 'center',
                background: '#141414',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: 2,
              }}
            >
              <Typography sx={{ color: '#555' }}>No events match the selected filter.</Typography>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 3,
                background: 'linear-gradient(145deg, #161616 0%, #1b1b1b 100%)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 2.5,
              }}
            >
              {visibleSchedule.map((event, idx) => {
                const cfg = STATUS_CONFIG[event.status];
                const typeCfg = TYPE_CONFIG[event.type] ?? { color: '#888', label: event.type };
                const canComplete = ['OVERDUE', 'DUE NOW', 'UPCOMING'].includes(event.status);
                const isLast = idx === visibleSchedule.length - 1;
                const isCompleted = event.status === 'COMPLETED';

                return (
                  <Box key={event.key} sx={{ display: 'flex', gap: 1.5 }}>
                    {/* ── Timeline node ── */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: 20,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: isCompleted ? 14 : 12,
                          height: isCompleted ? 14 : 12,
                          borderRadius: '50%',
                          bgcolor:
                            isCompleted || event.status === 'DUE NOW' ? cfg.color : 'transparent',
                          border:
                            isCompleted || event.status === 'DUE NOW'
                              ? 'none'
                              : `2px solid ${cfg.color}`,
                          flexShrink: 0,
                          mt: '15px',
                          zIndex: 1,
                          transition: 'all 0.2s',
                          ...(event.status === 'OVERDUE' && {
                            '@keyframes overdueGlow': {
                              '0%, 100%': { boxShadow: `0 0 0 0 ${cfg.color}55` },
                              '50%': { boxShadow: `0 0 0 5px ${cfg.color}00` },
                            },
                            animation: 'overdueGlow 2s ease-in-out infinite',
                          }),
                        }}
                      />
                      {!isLast && (
                        <Box
                          sx={{ width: 1.5, flex: 1, bgcolor: 'rgba(255,255,255,0.05)', my: 0.5 }}
                        />
                      )}
                    </Box>

                    {/* ── Event card ── */}
                    <Box
                      sx={{
                        flex: 1,
                        mb: isLast ? 0 : 1,
                        opacity: event.status === 'SCHEDULED' ? 0.5 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          bgcolor: cfg.bg,
                          border: `1px solid ${cfg.color}18`,
                          borderLeft: `3px solid ${typeCfg.color}`,
                          transition: 'border-color 0.15s',
                          '&:hover': {
                            border: `1px solid ${cfg.color}30`,
                            borderLeftColor: typeCfg.color,
                          },
                        }}
                      >
                        {/* Row 1: day badge + name + status chip */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {/* Day badge — fixed width so row 2 can align to it */}
                          <Box
                            sx={{
                              flexShrink: 0,
                              width: 52,
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                color: cfg.color,
                                fontWeight: 800,
                                fontSize: '1.05rem',
                                lineHeight: 1,
                              }}
                            >
                              {event.day}
                            </Typography>
                            <Typography
                              sx={{
                                color: 'rgba(255,255,255,0.3)',
                                fontWeight: 700,
                                fontSize: '0.55rem',
                                letterSpacing: '0.5px',
                              }}
                            >
                              DAY
                            </Typography>
                          </Box>

                          {/* Divider — must be '1px' string; width:1 = 100% in MUI sx */}
                          <Box
                            sx={{
                              width: '1px',
                              height: 22,
                              bgcolor: 'rgba(255,255,255,0.06)',
                              flexShrink: 0,
                            }}
                          />

                          {/* Name */}
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontWeight: 700,
                              fontSize: '0.86rem',
                              color: isCompleted ? '#555' : '#e8e8e8',
                              textDecoration: isCompleted ? 'line-through' : 'none',
                              lineHeight: 1.3,
                            }}
                          >
                            {event.name}
                          </Typography>

                          {/* Status chip */}
                          <Chip
                            label={cfg.label}
                            size="small"
                            sx={{
                              flexShrink: 0,
                              bgcolor: `${cfg.color}18`,
                              color: cfg.color,
                              fontWeight: 700,
                              fontSize: '0.62rem',
                              height: 20,
                              minWidth: 72,
                              border: `1px solid ${cfg.color}33`,
                            }}
                          />
                        </Box>

                        {/* Row 2: desc + meta + complete button — indented to align under name */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.75,
                            pl: `${52 + 12 + 1 + 12}px`, // day(52) + gap(12) + divider(1) + gap(12)
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', flex: 1 }}
                          >
                            {event.desc}
                          </Typography>

                          <Chip
                            label={typeCfg.label}
                            size="small"
                            sx={{
                              bgcolor: `${typeCfg.color}14`,
                              color: typeCfg.color,
                              fontWeight: 700,
                              fontSize: '0.6rem',
                              height: 18,
                              border: `1px solid ${typeCfg.color}28`,
                            }}
                          />

                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.35)',
                              fontSize: '0.68rem',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatDate(event.scheduled_date)}
                          </Typography>

                          {canComplete && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                <MarkCompleteIcon sx={{ fontSize: '0.85rem !important' }} />
                              }
                              onClick={() =>
                                selectedBatch && setConfirmDialog({ batch: selectedBatch, event })
                              }
                              disabled={mutation.isPending}
                              sx={{
                                color: '#4caf50',
                                borderColor: 'rgba(76,175,80,0.35)',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                py: 0.25,
                                px: 1.25,
                                minWidth: 0,
                                height: 24,
                                borderRadius: 1,
                                textTransform: 'none',
                                '&:hover': {
                                  bgcolor: 'rgba(76,175,80,0.1)',
                                  borderColor: 'rgba(76,175,80,0.6)',
                                },
                              }}
                            >
                              Complete
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Paper>
          )}
        </>
      )}

      {/* ── Confirm dialog ── */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
              border: '1px solid rgba(76,175,80,0.25)',
              borderRadius: 2.5,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 700, pb: 1 }}>Confirm Completion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
            Mark <strong style={{ color: '#ffa726' }}>{confirmDialog?.event.name}</strong> as
            completed for batch{' '}
            <strong style={{ color: '#ffa726' }}>{confirmDialog?.batch.batch}</strong>?
            <br />
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem' }}>
              This cannot be undone.
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog(null)}
            sx={{ color: '#555', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#888' } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              confirmDialog && handleMarkComplete(confirmDialog.batch, confirmDialog.event)
            }
            disabled={mutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
              color: '#fff',
              fontWeight: 700,
              px: 2.5,
              '&:hover': { background: 'linear-gradient(135deg, #66bb6a, #388e3c)' },
            }}
          >
            {mutation.isPending ? 'Saving…' : 'Mark as Completed'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VaccinationCalendar;
